var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { getFirstAnchorWithName } from "apparatus/common";
import { addMarkers, mirrorX, setDefaultStyle } from "apparatus/draw";
import { createApparatus } from "apparatus/library";
import { P, Pp, Segments } from "apparatus/library/common";
import { t_add } from "i18n_utils";
import { Group, Path } from "paper";
import { staticImplements } from "types";
const HUB_COLORS = {
    "Green": "#519e82",
    "Yellow": "#e8d387",
    "Black": "#414040",
    "Blue": "#2c82a2",
    "Pink": "#be94aa",
    "Purple": "#a185c7",
};
const DEMO_COLOURS = {
    "Green": "#519e82",
    "Yellow": "#e8d387",
    "Black": "#414040",
    "Blue": "#2c82a2",
};
export class Syringe {
    render(appearance) {
        let length = appearance.size;
        let diameter = 16;
        let outletLength = 3;
        let radius = diameter / 2;
        let plungerRadius = radius;
        let outletRadius = 3;
        let body = new Path(Segments(
        // Start from the tip
        [[outletRadius, length + outletLength]], [[outletRadius, length]], [[radius, length - 3], [0, 3]], // Slight rounded corner
        [[radius, 0], undefined, [30, -2]]));
        mirrorX(body);
        let liquidMask = body.clone();
        setDefaultStyle(body);
        // Plunger.
        let plunger = new Path(Segments([[plungerRadius, length - 5], [0, 3]], [[plungerRadius, -2], undefined, [20, -1]]));
        mirrorX(plunger);
        plunger.closePath();
        setDefaultStyle(plunger);
        plunger.fillColor = "#eeeeee";
        plunger.position.y -= appearance.plungerSlide * length;
        let accessory;
        if (appearance.cap) {
            let cap = new Path(Segments([[5, 0]], [[5, 7]], [[6, 8]], [[6, 11]])).mirrorX()
                .close()
                .withPositionDelta({ y: length + 4 })
                .withStroke("thin")
                .withGradientFill("down", [
                [appearance.hubColor, 0.6],
                [appearance.hubColor.color().darken(0.2).hex(), 0.7],
            ]);
            accessory = cap;
        }
        else {
            let needle = createNeedle(appearance.hubColor, appearance.needleSize);
            needle.position.y += length;
            accessory = needle;
        }
        let graphic = new Group([body, plunger, accessory]);
        addMarkers(graphic, P(plungerRadius - 5, length - 2), 0.3 * length, P(0, 10));
        return {
            graphic: graphic,
            liquidMask: liquidMask,
            hitShape: graphic.bounds.expand(10).toShape(),
            pivot: body.bounds.center,
            anchors: [
                {
                    name: "tip_adjusted",
                    at: appearance.cap ? accessory.bounds.bottomCenter : accessory.bounds.bottomCenter.subtract(P(0, 12))
                }
            ]
        };
    }
}
Syringe.properties = {
    label: "Syringe",
    canContainLiquids: true,
    defaultAppearance: {
        plungerSlide: 0.4,
        size: 90,
        needleSize: 30,
        hubColor: "#519e82",
        cap: false
    },
    appearanceSpecs: [
        {
            spec: "slider", key: "size", label: "Size",
            min: 60, max: 120
        },
        {
            spec: "slider", key: "plungerSlide", label: "Plunger slide",
            min: 0.0, max: 0.7, step: 0.01,
        },
        {
            spec: "checkbox", key: "cap", label: "Cap"
        },
        {
            spec: "slider", key: "needleSize", label: "Needle size",
            min: 20, max: 50,
            visible: a => !a.cap
        },
        { spec: "color", key: "hubColor", label: "Hub colour", swatches: HUB_COLORS, tier: "boost", demoSwatches: DEMO_COLOURS },
    ],
    actions: [
        {
            type: "add",
            icon: "plus",
            label: () => t_add("syringe filter"),
            action(a, p, r, { anchors }) {
                var _a, _b;
                let position = (_b = (_a = getFirstAnchorWithName(anchors, "tip_adjusted")) === null || _a === void 0 ? void 0 : _a.at) !== null && _b !== void 0 ? _b : Pp(p);
                let apparatus = createApparatus("syringe filter", position);
                apparatus.rotation = r;
                return apparatus;
            }
        }
    ]
};
let Needle = class Needle {
    render(appearance) {
        const graphic = createNeedle(appearance.hubColor, appearance.needleSize);
        return {
            graphic,
            hitShape: graphic.rectHitShape(),
            pivot: graphic.children[1].bounds.center,
        };
    }
};
Needle.properties = {
    label: "Needle",
    defaultAppearance: {
        needleSize: 30,
        hubColor: "#519e82"
    },
    appearanceSpecs: [
        {
            spec: "slider", key: "needleSize", label: "Needle size",
            min: 20, max: 50
        },
        { spec: "color", key: "hubColor", label: "Hub colour", swatches: HUB_COLORS, tier: "boost", demoSwatches: DEMO_COLOURS },
    ],
};
Needle = __decorate([
    staticImplements()
], Needle);
export { Needle };
function createNeedle(hubColor, needleSize) {
    let hub = new Path(Segments([[5, 2]], [[4, 13]], [[3, 14]], [[2, 22]]));
    mirrorX(hub);
    hub.closePath();
    hub.fillColor = hubColor;
    let needle = new Path(Segments([[0, 2]], [[0, needleSize + 20]]));
    needle.strokeWidth = 2.0;
    needle.strokeColor = "black";
    return new Group([needle, hub]);
}
